import { FC, useEffect, useRef } from "react"
import { getPDFJS, pdfWorker } from "ramper"
import { IconAudio, IconJSON, Image, Loader } from "dash-styled"
import { ReactComponent as IconPlay } from "icons/lib/play.svg"
import { ReactComponent as IconSatellite } from "icons/lib/satellite.svg"
import { hasValue } from "../../../Models/Client/FetchState"
import { useBoolean } from "../../../Hooks/useBoolean"
import { useSignedUrl } from "../../../Hooks/useSignedUrl"
import styles from "./Thumbnail.module.css"
import { isDataUrl } from "../../../Models/API/DataModel"
import { FileType } from "../../../Models/Client/FileType"

const DocumentData = ({ url }: { url: string }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)

    useEffect(() => {
        ;(async function () {
            const pdfjs = await getPDFJS()

            if (!pdfWorker) {
                return
            }

            const pdf = await pdfjs.getDocument({
                url,
                worker: pdfWorker
            }).promise

            const page = await pdf.getPage(1)
            const initViewport = page.getViewport({ scale: 1 })

            const canvas = canvasRef.current

            if (!canvas) {
                return
            }

            const canvasContext = canvas.getContext("2d")

            if (!canvasContext) {
                return
            }

            const scale = Math.min(
                canvas.width / initViewport.width,
                canvas.height / initViewport.height
            )
            const viewport = page.getViewport({ scale })

            canvas.width = viewport.width
            canvas.height = viewport.height

            // Render PDF page into canvas context.
            const renderContext = {
                canvasContext,
                viewport
            }
            page.render(renderContext)
        })()
    }, [canvasRef, url])

    return <canvas className="m-auto" width="46" height="46" ref={canvasRef} />
}

const ImageData = ({ url }: { url: string }) => {
    const [isLoaded, setToLoaded, setToLoading] = useBoolean(false)
    const signedUrlFetch = useSignedUrl(url)

    useEffect(() => {
        setToLoading()
    }, [setToLoading, signedUrlFetch])

    return (
        <div className={styles.ImageData}>
            {hasValue(signedUrlFetch) && (
                <Image
                    imageURL={signedUrlFetch.value}
                    onLoad={setToLoaded}
                    crossOrigin={
                        // Need to set cross-origin so that cached image without CORS headers
                        // doesn't cause error in loading images in annotation tools.
                        // See: https://bugs.chromium.org/p/chromium/issues/detail?id=158131
                        // We can only set cross-origin for data urls because we don't know external hosts support CORS requests.
                        isDataUrl(url)
                    }
                    allowFullscreen={false}
                />
            )}
            {isLoaded ? null : <Loader absolute={true} size="xxxl" />}
        </div>
    )
}

export const Thumbnail: FC<{
    type?: FileType
    value: unknown
    className?: string
}> = ({ type, value, className }) => {
    const cls = `${
        className ?? ""
    } w-12 h-12 border border-solid border-smoke-200`
    const iconClassName = `${cls} p-2`

    switch (type) {
        case "image":
            return (
                <div className={cls}>
                    {typeof value === "string" && <ImageData url={value} />}
                </div>
            )
        case "audio":
            return <IconAudio className={iconClassName} size="md" />
        case "video":
            return <IconPlay className={`w-4 h-4 ${iconClassName}`} />
        case "satellite":
            return <IconSatellite className={iconClassName} />
        case "document": {
            return (
                <div className={cls}>
                    {typeof value === "string" && <DocumentData url={value} />}
                </div>
            )
        }
        default:
            return <IconJSON className={iconClassName} />
    }
}
